// Custom connectors: the list of operator-authored collector scripts, their
// health, and the door into the editor.
//
// Backend contract: docs/battex/10-custom-collectors.md.

import React, { useCallback, useEffect, useRef, useState } from "react";

import { getCollectorStatus, listCollectors, setCollectorEnabled } from "../../../api";
import plusIcon from "../../../assets/plus.svg";
import { useNotify } from "../../../ToastContext";
import { usePermission } from "../../../UserContext";
import { CustomCollectorEditor } from "./CustomCollectorEditor";
import { collectorStatus, formatPollInterval, formatRelativeTime } from "./collectors-model";

const STATUS_POLL_MS = 10_000;

const errorMessage = (error, fallback) => String(error?.message || error || "").trim() || fallback;

// The status endpoint answers a different shape than the list: same status
// block, but `outputs` is a count where the list carries the array. Merging
// field by field keeps the row's outputs from being overwritten by a number.
const mergeStatus = (collector, status) => ({
  ...collector,
  name: status.name ?? collector.name,
  enabled: status.enabled,
  poll_interval_ms: status.poll_interval_ms,
  timeout_ms: status.timeout_ms,
  last_run_at: status.last_run_at,
  last_success_at: status.last_success_at,
  last_duration_ms: status.last_duration_ms,
  last_error: status.last_error,
  consecutive_failures: status.consecutive_failures,
  last_values: status.last_values,
});

export function CustomCollectorsPage() {
  const canConfigure = usePermission("configure");
  const notify = useNotify();
  const [collectors, setCollectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(null);
  const [togglingId, setTogglingId] = useState("");
  const collectorsRef = useRef([]);

  useEffect(() => {
    collectorsRef.current = collectors;
  }, [collectors]);

  const load = useCallback(async () => {
    try {
      const list = await listCollectors();
      setCollectors(Array.isArray(list) ? list : []);
      setLoadError("");
    } catch (error) {
      setLoadError(errorMessage(error, "No se pudieron cargar los colectores."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Cheap refresh of just the status block. Skipped while the tab is in the
  // background: a collector nobody is looking at does not need a request every
  // ten seconds from every open gateway.
  const refreshStatus = useCallback(async () => {
    if (typeof document !== "undefined" && document.hidden) return;
    const ids = collectorsRef.current.map((collector) => collector.id);
    if (ids.length === 0) return;
    const statuses = await Promise.all(
      ids.map((id) => getCollectorStatus(id).catch(() => null))
    );
    const byID = new Map(statuses.filter(Boolean).map((status) => [status.id, status]));
    setCollectors((current) =>
      current.map((collector) =>
        byID.has(collector.id) ? mergeStatus(collector, byID.get(collector.id)) : collector
      )
    );
  }, []);

  useEffect(() => {
    const timer = setInterval(refreshStatus, STATUS_POLL_MS);
    return () => clearInterval(timer);
  }, [refreshStatus]);

  const toggleEnabled = async (collector) => {
    setTogglingId(collector.id);
    try {
      const updated = await setCollectorEnabled(collector.id, !collector.enabled);
      setCollectors((current) =>
        current.map((row) => (row.id === updated.id ? { ...row, ...updated } : row))
      );
    } catch (error) {
      notify({
        type: "error",
        title: collector.enabled ? "Disable failed" : "Enable failed",
        message: errorMessage(error, "No se pudo cambiar el estado del colector."),
      });
    } finally {
      setTogglingId("");
    }
  };

  const hasCollectors = collectors.length > 0;

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Custom connectors</div>
            <div className="gwPageSubtitle">
              Scripts de Python que el gateway corre en su propio horario; cada value que
              devuelven queda en un tag normal, con su historian normal.
            </div>
          </div>
          <button
            className="gwPrimaryBtn gwCcNewBtn"
            type="button"
            disabled={!canConfigure}
            title={canConfigure ? undefined : "Requires configure permission"}
            onClick={() => setEditing({ collector: null })}
          >
            <img src={plusIcon} alt="" className="gwIcon gwCcNewIcon" />
            New collector
          </button>
        </div>

        {loadError ? (
          <div className="gwAlert" role="alert">
            <div className="gwAlertTitle">Colectores no disponibles</div>
            <div className="gwAlertBody">{loadError}</div>
          </div>
        ) : null}

        {loading && !hasCollectors ? <div className="gwCcEmpty">Cargando colectores…</div> : null}

        {!loading && !hasCollectors && !loadError ? (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">Todavía no hay colectores</div>
            <div className="gwEmptyBody">
              Un colector es un script corto más un intervalo. Empieza desde la plantilla,
              pruébalo con Run now y recién ahí habilítalo.
            </div>
            <div className="gwEmptyActions">
              <button
                className="gwPrimaryBtn"
                type="button"
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
                onClick={() => setEditing({ collector: null })}
              >
                New collector
              </button>
            </div>
          </div>
        ) : null}

        {hasCollectors ? (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Collectors</div>
            <div className="gwCardSectionBody gwCcListBody">
              {collectors.map((collector) => {
                const status = collectorStatus(collector);
                const outputs = Array.isArray(collector.outputs) ? collector.outputs.length : 0;
                return (
                  <div
                    className="gwCcRow"
                    key={collector.id}
                    data-gw-resource="custom_collectors"
                    data-gw-id={collector.id}
                    data-gw-name={collector.name}
                  >
                    <span
                      className={`gwCcDot is-${status.key}`}
                      title={status.label}
                      aria-label={status.label}
                      role="img"
                    />
                    <button
                      type="button"
                      className="gwCcRowMain"
                      onClick={() => setEditing({ collector })}
                    >
                      <span className="gwCcRowName">{collector.name}</span>
                      <span className="gwCcRowMeta">
                        <span>{formatPollInterval(collector.poll_interval_ms)}</span>
                        <span>
                          {outputs} output{outputs === 1 ? "" : "s"}
                        </span>
                        <span>Último run: {formatRelativeTime(collector.last_run_at)}</span>
                        {collector.last_error ? (
                          <span className="gwCcRowError">{collector.last_error}</span>
                        ) : null}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`gwToggle ${collector.enabled ? "isOn" : ""}`}
                      role="switch"
                      aria-checked={collector.enabled}
                      aria-label={`Enable ${collector.name}`}
                      disabled={!canConfigure || togglingId === collector.id}
                      onClick={() => toggleEnabled(collector)}
                    >
                      <span />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </section>

      {editing ? (
        <CustomCollectorEditor
          collector={editing.collector}
          canConfigure={canConfigure}
          onClose={() => setEditing(null)}
          onSaved={() => load()}
          onDeleted={() => load()}
        />
      ) : null}
    </div>
  );
}

export default CustomCollectorsPage;
